import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Camera, Ruler, RotateCcw, Hand, Coins } from "lucide-react";
import { toast } from "sonner";
import { FilesetResolver, HandLandmarker } from "@mediapipe/tasks-vision";

const WASM_URL = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm";
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task";

const FINGERS = ["thumb", "index", "middle", "ring", "pinky"];
const FINGER_LABELS = { thumb: "Thumb", index: "Index", middle: "Middle", ring: "Ring", pinky: "Pinky" };

const CHART = [
  {
    size: "Small",
    code: "S",
    widths: { thumb: "15–16", index: "11–12", middle: "12–13", ring: "11–12", pinky: "8–9" },
    max: { thumb: 16, index: 12, middle: 13, ring: 12, pinky: 9 },
    copy: "Petite nail beds — our Small set is sculpted for you.",
  },
  {
    size: "Medium",
    code: "M",
    widths: { thumb: "16–17", index: "12–13", middle: "13–14", ring: "12–13", pinky: "9–10" },
    max: { thumb: 17, index: 13, middle: 14, ring: 13, pinky: 10 },
    copy: "The classic fit — our Medium set will sit like it was painted on.",
  },
  {
    size: "Large",
    code: "L",
    widths: { thumb: "17–18.5", index: "13–14.5", middle: "14–15.5", ring: "13–14.5", pinky: "10–11" },
    max: null,
    copy: "Beautifully broad nail beds — our Large set is your match.",
  },
];

const COIN_DIAMETER_MM = 27; // ₹10 coin
const COIN_CIRCLE_FRACTION = 0.1; // of frame width
const QUICK_MM_PER_UNIT = 300; // rough estimate when no coin reference is used

const classifyFinger = (finger, mm) => {
  if (mm <= CHART[0].max[finger]) return "S";
  if (mm <= CHART[1].max[finger]) return "M";
  return "L";
};

const steps = [
  "Enable the camera — everything is measured on your device, nothing is uploaded.",
  "For true millimetre accuracy, rest a ₹10 coin beside your hand inside the circle guide.",
  "Hold your hand flat, fingers open, tap measure.",
];

export default function NailSize() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const landmarkerRef = useRef(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coinMode, setCoinMode] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(
    () => () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    },
    []
  );

  const startCamera = async () => {
    setBusy(true);
    try {
      if (!landmarkerRef.current) {
        const vision = await FilesetResolver.forVisionTasks(WASM_URL);
        landmarkerRef.current = await HandLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
          runningMode: "VIDEO",
          numHands: 1,
        });
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setResult(null);
      setCameraOn(true);
    } catch {
      toast.error("Camera unavailable — please allow permission and try again.");
    }
    setBusy(false);
  };

  const measure = async () => {
    const video = videoRef.current;
    if (!video || !landmarkerRef.current || busy) return;
    setBusy(true);
    setResult(null);
    const frames = [];
    for (let i = 0; i < 8; i++) {
      const res = landmarkerRef.current.detectForVideo(video, performance.now());
      if (res.landmarks?.length) frames.push(res.landmarks[0]);
      await new Promise((r) => setTimeout(r, 120));
    }
    setBusy(false);
    if (frames.length < 4) {
      toast.error("No hand found — place your open palm inside the guide and try again.");
      return;
    }
    const avg = Array.from({ length: 21 }, (_, j) => ({
      x: frames.reduce((a, f) => a + f[j].x, 0) / frames.length,
      y: frames.reduce((a, f) => a + f[j].y, 0) / frames.length,
    }));
    const d = (a, b) => Math.hypot(avg[a].x - avg[b].x, avg[a].y - avg[b].y);
    const spanIndex = d(5, 9);
    const spanMiddle = d(9, 13);
    const spanRing = d(13, 17);
    const norm = {
      index: spanIndex * 0.62,
      middle: spanMiddle * 0.66,
      ring: spanRing * 0.72,
      pinky: spanRing * 0.5,
    };
    norm.thumb = norm.index * 1.35;
    const mmPerUnit = coinMode ? COIN_DIAMETER_MM / COIN_CIRCLE_FRACTION : QUICK_MM_PER_UNIT;
    const perFinger = FINGERS.map((finger) => {
      const mm = Math.round(norm[finger] * mmPerUnit * 10) / 10;
      return { finger, mm, size: classifyFinger(finger, mm) };
    });
    const tally = { S: 0, M: 0, L: 0 };
    perFinger.forEach((p) => tally[p.size]++);
    const middleSize = perFinger[2].size;
    const overall =
      tally.S === tally.M && tally.M === tally.L
        ? middleSize
        : Object.entries(tally).sort((a, b) => b[1] - a[1])[0][1] === tally[middleSize]
          ? middleSize
          : Object.entries(tally).sort((a, b) => b[1] - a[1])[0][0];
    setResult({ overall, perFinger, estimated: !coinMode });
  };

  const reset = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOn(false);
    setResult(null);
  };

  const overallRow = result ? CHART.find((c) => c.code === result.overall) : null;

  return (
    <section id="size-guide" className="border-t border-[#26221F]/15 px-6 py-24 lg:px-12 lg:py-32" data-testid="nail-size-section">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <p className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.4em] text-[#6F665F]">
              <Ruler className="h-4 w-4 text-[#D8AFA4]" /> The Fitting Room
            </p>
            <h2 className="font-display text-4xl leading-none tracking-tight md:text-6xl" data-testid="nail-size-heading">
              Know <em className="italic text-[#D8AFA4]">your</em> nail size
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-[#6F665F] md:text-lg">
              Show your hand to the camera and our on-device AI will read each
              nail bed in millimetres, then match you to the Her Label size
              chart — Small, Medium or Large.
            </p>
            <ol className="mt-10 space-y-5">
              {steps.map((s, i) => (
                <li key={i} className="flex items-start gap-4" data-testid={`nail-size-step-${i + 1}`}>
                  <span className="font-display text-2xl italic leading-none text-[#D8AFA4]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm leading-relaxed text-[#6F665F]">{s}</span>
                </li>
              ))}
            </ol>
            <div className="mt-10 max-w-md border border-[#26221F]/15 bg-[#F3E6E1] p-5 text-xs leading-relaxed text-[#6F665F]">
              For the most accurate fit, we recommend using our 10-piece sizing
              kit before ordering.
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <div className="relative overflow-hidden border border-[#26221F]/20 bg-[#F3E6E1]" data-testid="nail-size-viewfinder">
              {!cameraOn && (
                <div className="flex aspect-[4/3] flex-col items-center justify-center gap-6 px-8 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#FAF7F2] border border-[#26221F]/15">
                    <Hand className="h-7 w-7 text-[#D8AFA4]" />
                  </span>
                  <p className="max-w-xs font-display text-xl italic text-[#6F665F]">
                    Your camera stays yours — measuring happens on your device only.
                  </p>
                  <button
                    onClick={startCamera}
                    disabled={busy}
                    data-testid="nail-size-enable-camera"
                    className="flex items-center gap-3 rounded-full bg-[#26221F] px-8 py-4 text-xs font-semibold uppercase tracking-[0.3em] text-[#FAF7F2] transition-colors duration-300 hover:bg-[#D8AFA4] hover:text-[#26221F] disabled:opacity-50"
                  >
                    <Camera className="h-4 w-4" /> {busy ? "Preparing…" : "Enable Camera"}
                  </button>
                </div>
              )}
              <div className={cameraOn ? "block" : "hidden"}>
                <div className="relative">
                  <video ref={videoRef} playsInline muted className="aspect-[4/3] w-full -scale-x-100 object-cover" data-testid="nail-size-video" />
                  <div className="pointer-events-none absolute inset-6 rounded-3xl border-2 border-dashed border-[#FAF7F2]/80" aria-hidden="true" />
                  {coinMode && (
                    <div
                      className="pointer-events-none absolute bottom-10 left-10 flex aspect-square items-center justify-center rounded-full border-2 border-dashed border-[#D8AFA4]"
                      style={{ width: `${COIN_CIRCLE_FRACTION * 100}%` }}
                      aria-hidden="true"
                      data-testid="nail-size-coin-guide"
                    >
                      <span className="text-[8px] font-semibold uppercase tracking-[0.15em] text-[#FAF7F2] drop-shadow">₹10 coin</span>
                    </div>
                  )}
                  <p className="pointer-events-none absolute bottom-4 left-0 right-0 text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FAF7F2] drop-shadow">
                    Fit your open hand inside the frame
                  </p>
                  {result && overallRow && (
                    <motion.div
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute inset-x-6 bottom-12 border border-[#26221F]/15 bg-[#FAF7F2]/95 p-6 text-center backdrop-blur"
                      data-testid="nail-size-result"
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-[#6F665F]">
                        {result.estimated ? "Her rough estimate" : "Her prediction"}
                      </p>
                      <p className="mt-2 font-display text-5xl italic tracking-tight" data-testid="nail-size-result-value">
                        {overallRow.size}
                      </p>
                      <div className="mt-4 flex flex-wrap justify-center gap-2">
                        {result.perFinger.map((p) => (
                          <span
                            key={p.finger}
                            className="rounded-full border border-[#26221F]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#6F665F]"
                            data-testid={`nail-size-finger-${p.finger}`}
                          >
                            {FINGER_LABELS[p.finger]} {p.mm} mm · {p.size}
                          </span>
                        ))}
                      </div>
                      <p className="mt-3 text-xs text-[#6F665F]">{overallRow.copy}</p>
                    </motion.div>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-center gap-4 border-t border-[#26221F]/15 bg-[#FAF7F2] px-6 py-5">
                  <button
                    onClick={() => setCoinMode((c) => !c)}
                    data-testid="nail-size-coin-toggle"
                    className={`flex items-center gap-2 rounded-full border px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                      coinMode
                        ? "border-[#26221F] bg-[#26221F] text-[#FAF7F2]"
                        : "border-[#26221F]/25 text-[#26221F] hover:bg-[#F3E6E1]"
                    }`}
                  >
                    <Coins className="h-4 w-4" /> {coinMode ? "₹10 coin placed" : "Use ₹10 coin for accuracy"}
                  </button>
                  <button
                    onClick={measure}
                    disabled={busy}
                    data-testid="nail-size-measure-button"
                    className="flex items-center gap-3 rounded-full bg-[#26221F] px-8 py-4 text-xs font-semibold uppercase tracking-[0.3em] text-[#FAF7F2] transition-colors duration-300 hover:bg-[#D8AFA4] hover:text-[#26221F] disabled:opacity-50"
                  >
                    <Ruler className="h-4 w-4" /> {busy ? "Reading your hand…" : "Measure My Size"}
                  </button>
                  <button
                    onClick={reset}
                    data-testid="nail-size-reset-button"
                    aria-label="Turn off camera and start over"
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-[#26221F]/25 text-[#26221F] transition-colors duration-300 hover:bg-[#26221F] hover:text-[#FAF7F2]"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-24"
          data-testid="size-chart"
        >
          <div className="mb-12 max-w-2xl">
            <p className="mb-6 text-xs font-semibold uppercase tracking-[0.4em] text-[#6F665F]">
              Indian Market Sizing
            </p>
            <h3 className="font-display text-3xl leading-tight tracking-tight md:text-5xl" data-testid="size-chart-heading">
              The size chart, <em className="italic text-[#D8AFA4]">in millimetres</em>
            </h3>
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-[#6F665F]">
              Widths are measured across each press-on nail at its widest point.
              To find yours, measure your natural nail from sidewall to sidewall
              at the widest point.
            </p>
          </div>
          <div className="overflow-x-auto border border-[#26221F]/20" data-testid="size-chart-table">
            <table className="w-full min-w-[720px] border-collapse text-left">
              <thead>
                <tr className="bg-[#26221F] text-[#FAF7F2]">
                  <th className="px-6 py-5 text-[10px] font-semibold uppercase tracking-[0.3em]">Size</th>
                  {FINGERS.map((f) => (
                    <th key={f} className="px-6 py-5 text-[10px] font-semibold uppercase tracking-[0.3em]">
                      {FINGER_LABELS[f]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CHART.map((row, i) => (
                  <tr
                    key={row.code}
                    className={i % 2 === 0 ? "bg-[#FAF7F2]" : "bg-[#F3E6E1]"}
                    data-testid={`size-chart-row-${row.code.toLowerCase()}`}
                  >
                    <td className="px-6 py-6">
                      <span className="font-display text-2xl italic tracking-tight">{row.size}</span>
                      <span className="ml-3 text-xs font-semibold uppercase tracking-[0.25em] text-[#6F665F]">{row.code}</span>
                    </td>
                    {FINGERS.map((f) => (
                      <td key={f} className="px-6 py-6 font-display text-xl tracking-tight">
                        {row.widths[f]}
                        <span className="ml-1 text-xs text-[#6F665F]">mm</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="border border-[#26221F]/15 bg-[#FAF7F2] p-6 text-sm leading-relaxed text-[#6F665F]" data-testid="size-chart-note-measure">
              Measurements are the width of each press-on nail at its widest
              point, in millimetres. Measure your natural nail from sidewall to
              sidewall at the widest point — a soft measuring tape works best.
            </div>
            <div className="border border-[#26221F]/15 bg-[#F3E6E1] p-6 text-sm leading-relaxed text-[#26221F]" data-testid="size-chart-note-kit">
              For the most accurate fit, we recommend using our 10-piece sizing
              kit before ordering.
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
